// Dlaždice seznamu: stejná data a výběr jako tabulka (IwTable), jiné vykreslení – karta s obrázkem/iniciálami,
// názvem, podtitulem, dalšími řádky a odznakem. Které sloupce kam patří říká okno (CardDef).
import { h, clear } from '../dom';
import { icon } from '../icons';
import { contextMenu, type MenuItem } from './menu';
import type { CardDef } from '../contract';
import { setRowsDrag, type Row } from './table';
import { isPh, vwindow, navTarget, rangeIds, type VWindow } from './virtual';

/** Iniciály jen z písmen (ne „P(“ z „Přijato (…“). */
export const initials = (s: string): string => s.split(/\s+/).filter((w) => /^\p{L}/u.test(w)).slice(0, 2).map((w) => w.charAt(0).toUpperCase()).join('') || '•';

export class IwCards extends HTMLElement {
  private rows: Row[] = [];
  private rowId = 'id';
  private def: CardDef | null = null;
  private selected = new Set<string>();
  private anchor: string | null = null;
  /** položka pod kurzorem klávesnice (šipky) */
  private cursor: string | null = null;
  /** kurzor míří na ještě nenačtenou položku – vybere se, až dorazí (updateRows) */
  private pending: { i: number; extend: boolean } | null = null;
  private emptyEl = h('div', { class: 'table-empty' });
  private grid = h('div', { class: 'cards-grid', role: 'listbox', 'aria-multiselectable': 'true' });
  emptyText = '';
  selectMode = false;
  /** Karty jde přetáhnout (stejná data jako řádky tabulky, viz ROWS_MIME). */
  rowsDraggable = false;
  onSelect: ((ids: string[]) => void) | null = null;
  onOpen: ((id: string, row: Row) => void) | null = null;
  onContext: ((ids: string[], e: MouseEvent | PointerEvent) => MenuItem[] | null) | null = null;
  /** Dlouhý stisk prstem: místo kontextové nabídky (telefon – zapne režim výběru, koncepce A). */
  onLongPress: ((id: string) => void) | null = null;
  /** Klepnutí (mimo režim výběru) záznam rovnou otevře – telefon. */
  tapOpens = false;
  private suppressUntil = 0;
  /** Plynulý seznam: vykreslí se jen okno kolem posunu, o nenačtené řádky si řekne přes onRange. */
  virtual = false;
  onRange: ((from: number, to: number) => void) | null = null;
  /** Shift-výběr přes nenačtené: dočíst řádky from..to (pohled) */
  loadRange: ((from: number, to: number) => Promise<void>) | null = null;
  private lineH = 0;
  private measuring = false;
  private win: VWindow = { start: -1, end: -1, padTop: 0, padBottom: 0 };
  private raf = 0;

  connectedCallback(): void {
    if (this.childElementCount) return;
    this.classList.add('cards');
    this.tabIndex = 0;
    this.append(this.grid, this.emptyEl);
    this.addEventListener('keydown', (e) => this.onKey(e));
    this.addEventListener('scroll', () => { if (this.virtual) this.schedule(); }, { passive: true });
    new ResizeObserver(() => { if (this.virtual) this.schedule(true); }).observe(this);
    contextMenu(this, (e) => {
      const card = (e.target as HTMLElement).closest<HTMLElement>('.card[data-id]');
      if (!card) return null;
      const id = card.dataset['id'] ?? '';
      if (!this.selected.has(id)) {
        this.selected.clear();
        this.selected.add(id);
        this.anchor = id;
        this.paint();
        this.onSelect?.(this.selection());
      }
      if (this.onLongPress) {
        const stop = () => { e.preventDefault(); e.stopPropagation(); }; // ani nabídka plochy pod oknem
        if ('pointerType' in e && e.pointerType === 'touch') {
          this.suppressUntil = Date.now() + 1200; // prohlížeč po stisku pošle ještě contextmenu a click
          this.onLongPress(id);
          stop();
          return null;
        }
        if (Date.now() < this.suppressUntil) {
          stop();
          return null;
        }
      }
      return this.onContext?.(this.selection(), e) ?? null;
    });
  }

  setCard(def: CardDef | null): void {
    this.def = def;
    this.render();
  }

  /** Mřížka dlaždic, nebo dvouřádkové řádky (seznam vedle detailu, telefon). */
  setLayout(layout: 'grid' | 'rows'): void {
    this.classList.toggle('rows', layout === 'rows');
    this.lineH = 0;
  }

  setSelectMode(on: boolean): void {
    this.selectMode = on;
    this.classList.toggle('select-mode', on);
    this.render();
  }

  setRows(rows: Row[], rowId: string): void {
    this.rows = rows;
    this.rowId = rowId;
    if (!this.virtual) {
      const ids = new Set(rows.map((r) => String(r[rowId])));
      for (const id of Array.from(this.selected)) if (!ids.has(id)) this.selected.delete(id);
    }
    this.render();
  }

  /** Plynulý seznam: dočtené řádky (stejná délka, zástupci nahrazení) – bez ztráty posunu a výběru. */
  updateRows(rows: Row[]): void {
    this.rows = rows;
    this.renderWindow(true);
    const p = this.pending;
    if (p && !isPh(this.rows[p.i])) this.moveTo(p.i, p.extend);
  }

  /** Výběr podle id (Vybrat všech N – i nenačtené). */
  selectIds(ids: string[]): void {
    for (const id of ids) this.selected.add(id);
    this.paint();
    this.onSelect?.(this.selection());
  }

  /** Nahradí výběr (vrácení výběru na záznam v detailu, prořezání po obnovení). */
  select(ids: string[]): void {
    const same = ids.length === this.selected.size && ids.every((id) => this.selected.has(id));
    if (same) return;
    this.selected = new Set(ids);
    if (this.anchor && !this.selected.has(this.anchor)) this.anchor = ids[0] ?? null;
    this.paint();
    this.onSelect?.(this.selection());
  }

  /** Skok na položku (abeceda A–Z). */
  scrollToIndex(i: number): void {
    const { cols, lineH } = this.metrics();
    this.scrollTop = Math.floor(i / cols) * lineH;
    this.renderWindow(true);
  }

  selection(): string[] {
    if (this.virtual) return Array.from(this.selected);
    return this.rows.map((r) => String(r[this.rowId])).filter((id) => this.selected.has(id));
  }

  selectedRows(): Row[] {
    return this.rows.filter((r) => this.selected.has(String(r[this.rowId])));
  }

  selectAll(): void {
    for (const r of this.rows) if (!isPh(r)) this.selected.add(String(r[this.rowId]));
    this.paint();
    this.onSelect?.(this.selection());
  }

  clearSelection(): void {
    this.selected.clear();
    this.paint();
    this.onSelect?.(this.selection());
  }

  /** Formát hodnoty podle sloupce (automatická vizitka seznamu bez vlastní – datum, výčet…). */
  format: ((key: string, row: Row) => string) | null = null;

  private text(row: Row, key: string | null | undefined): string {
    if (!key) return '';
    if (this.format) return this.format(key, row);
    const v = row[key];
    return v === null || v === undefined ? '' : String(v);
  }

  private render(): void {
    if (this.virtual) {
      this.renderWindow(true);
      return;
    }
    clear(this.grid);
    this.grid.style.paddingTop = this.grid.style.paddingBottom = '';
    this.emptyEl.textContent = this.rows.length ? '' : this.emptyText;
    this.emptyEl.hidden = this.rows.length > 0;
    if (!this.def) return;
    for (const row of this.rows) this.grid.appendChild(this.cardEl(row));
  }

  private schedule(force = false): void {
    if (force) this.lineH = this.lineH || 0;
    cancelAnimationFrame(this.raf);
    this.raf = requestAnimationFrame(() => this.renderWindow(force));
  }

  /** Počet dlaždic na řádek a výška řádku (změřená z vykreslené karty). */
  private metrics(): { cols: number; lineH: number; gap: number } {
    const rows = this.classList.contains('rows');
    const gap = rows ? 0 : 10;
    const cols = rows ? 1 : Math.max(1, Math.floor((this.grid.clientWidth + gap) / (230 + gap)));
    return { cols, lineH: this.lineH || (rows ? 58 : 92), gap };
  }

  private renderWindow(force: boolean): void {
    this.emptyEl.textContent = this.rows.length ? '' : this.emptyText;
    this.emptyEl.hidden = this.rows.length > 0;
    if (!this.def) return;
    const { cols, lineH, gap } = this.metrics();
    const w = vwindow(this.rows.length, cols, lineH, this.scrollTop, this.clientHeight || 600);
    if (!force && w.start === this.win.start && w.end === this.win.end) return;
    this.win = w;
    clear(this.grid);
    this.grid.style.paddingTop = w.padTop + 'px';
    this.grid.style.paddingBottom = w.padBottom + 'px';
    for (let i = w.start; i < w.end; i++) this.grid.appendChild(this.cardEl(this.rows[i] as Row));
    // výška řádku podle skutečných karet (písmo, telefon): nejvyšší z vykreslených, přeměřit nejvýš jednou –
    // v mřížce s různě vysokými kartami by se jinak okno a výška navzájem posouvaly donekonečna
    if (!this.measuring) {
      let mh = 0;
      this.grid.querySelectorAll<HTMLElement>('.card:not(.ph)').forEach((c) => { mh = Math.max(mh, c.offsetHeight + gap); });
      if (mh > 10 && Math.abs(mh - lineH) > 1) {
        this.lineH = mh;
        this.measuring = true;
        try {
          this.renderWindow(true);
        } finally {
          this.measuring = false;
        }
        return;
      }
    }
    this.onRange?.(w.start, w.end);
  }

  private cardEl(row: Row): HTMLElement {
    const d = this.def as CardDef;
    if (isPh(row)) return h('div', { class: 'card ph', 'aria-hidden': 'true' }, h('div', { class: 'card-media' }, h('span', { class: 'card-initials' })), h('div', { class: 'card-body' }, h('div', { class: 'ph-bar' }), h('div', { class: 'ph-bar short' })));
    {
      const id = String(row[this.rowId]);
      const title = this.text(row, d.title);
      const img = this.text(row, d.image);
      const media = img ? h('img', { class: 'card-img', src: img, alt: '', loading: 'lazy' })
        : d.media === 'initials' ? h('span', { class: 'card-initials' }, initials(title))
        : h('span', { class: 'card-initials card-icon' }, icon(d.icon ?? 'file')); // věc, ne osoba: ikona místo iniciál
      const lines = (d.lines ?? []).map((k) => this.text(row, k)).filter(Boolean).map((v) => h('div', { class: 'card-line' }, v));
      const badge = this.text(row, d.badge);
      const card = h('div', { class: 'card' + (this.selected.has(id) ? ' selected' : '') + (id === this.cursor ? ' cursor' : ''), role: 'option', 'aria-selected': this.selected.has(id) ? 'true' : 'false', dataset: { id }, title },
        this.selectMode ? h('span', { class: 'sel-check', 'aria-hidden': 'true' }, icon('check')) : null,
        h('div', { class: 'card-media' }, media),
        h('div', { class: 'card-body' }, h('div', { class: 'card-title' }, title || '—'), d.subtitle ? h('div', { class: 'card-sub' }, this.text(row, d.subtitle)) : null, lines, badge ? h('span', { class: 'badge card-badge' }, badge) : null),
      );
      card.addEventListener('click', (e) => this.onCardClick(e, id));
      card.addEventListener('dblclick', () => this.onOpen?.(id, row));
      if (this.rowsDraggable) {
        card.setAttribute('draggable', 'true');
        card.addEventListener('dragstart', (e) => {
          if (!this.selected.has(id)) {
            this.selected.clear();
            this.selected.add(id);
            this.anchor = id;
            this.paint();
            this.onSelect?.(this.selection());
          }
          setRowsDrag(e, this.selection(), title);
        });
      }
      return card;
    }
  }

  private onCardClick(e: MouseEvent, id: string): void {
    if (Date.now() < this.suppressUntil) return;
    if (this.tapOpens && !this.selectMode && !e.ctrlKey && !e.metaKey && !e.shiftKey) {
      const row = this.rows.find((r) => String(r[this.rowId]) === id);
      if (!row) return;
      this.selected.clear();
      this.selected.add(id); // příkaz Otevřít pracuje s výběrem
      this.anchor = id;
      this.onSelect?.(this.selection());
      this.onOpen?.(id, row);
      window.setTimeout(() => this.clearSelection(), 0);
      return;
    }
    this.cursor = id;
    if (this.selectMode || e.ctrlKey || e.metaKey) {
      if (this.selected.has(id)) this.selected.delete(id);
      else this.selected.add(id);
      this.anchor = id;
    } else if (e.shiftKey && this.anchor) {
      void this.selectRange(this.indexOf(this.anchor), this.indexOf(id));
      return;
    } else {
      this.selected.clear();
      this.selected.add(id);
      this.anchor = id;
    }
    this.paint();
    this.onSelect?.(this.selection());
  }

  private indexOf(id: string): number {
    return this.rows.findIndex((r) => String(r[this.rowId]) === id);
  }

  /** Rozsah od kotvy (Shift) podle pozice – i přes nenačtené položky. */
  private async selectRange(a: number, b: number): Promise<void> {
    if (a < 0 || b < 0) return;
    const ids = await rangeIds(() => this.rows, this.rowId, a, b, this.loadRange);
    this.selected = new Set(ids);
    this.paint();
    this.onSelect?.(this.selection());
  }

  /** Kurzor klávesnice na pozici i: vybrat ji (Shift = rozšířit od kotvy), posunout do zorného pole. */
  private moveTo(i: number, extend: boolean): void {
    const { cols, lineH } = this.metrics();
    const top = Math.floor(i / cols) * lineH;
    if (top < this.scrollTop) this.scrollTop = top;
    else if (top + lineH > this.scrollTop + this.clientHeight) this.scrollTop = top + lineH - this.clientHeight;
    const row = this.rows[i];
    if (isPh(row)) {
      this.pending = { i, extend }; // dočte se přes onRange, vybere se v updateRows
      this.renderWindow(false);
      return;
    }
    this.pending = null;
    const id = String((row as Row)[this.rowId]);
    this.cursor = id;
    if (extend && this.anchor) {
      void this.selectRange(this.indexOf(this.anchor), i);
      return;
    }
    this.selected.clear();
    this.selected.add(id);
    this.anchor = id;
    this.paint();
    this.onSelect?.(this.selection());
  }

  private paint(): void {
    this.grid.querySelectorAll<HTMLElement>('.card').forEach((c) => {
      const sel = this.selected.has(c.dataset['id'] ?? '');
      c.classList.toggle('selected', sel);
      c.setAttribute('aria-selected', sel ? 'true' : 'false');
      c.classList.toggle('cursor', c.dataset['id'] === this.cursor);
    });
  }

  private onKey(e: KeyboardEvent): void {
    if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'a') {
      e.preventDefault();
      this.selectAll();
      return;
    }
    const cur = this.cursor ?? this.anchor;
    if (e.key === 'Enter' && cur) {
      const row = this.rows.find((r) => String(r[this.rowId]) === cur);
      if (row) this.onOpen?.(cur, row);
      return;
    }
    if (e.key === 'Escape' && !this.virtual) { // plynulý seznam: Esc řeší pohled (rozpracovaný detail má přednost)
      this.clearSelection();
      return;
    }
    // šipky, Home/End, PageUp/PageDown: posun výběru (Shift = rozšířit), v mřížce i do stran
    const { cols, lineH } = this.metrics();
    const next = navTarget(e.key, cur ? this.indexOf(cur) : -1, this.rows.length, cols, Math.max(1, Math.floor(this.clientHeight / lineH) - 1) * cols);
    if (next === null || e.altKey || e.ctrlKey || e.metaKey) return;
    e.preventDefault();
    this.moveTo(next, e.shiftKey);
  }
}

customElements.define('iw-cards', IwCards);
